import { Fragment, useEffect, useState } from 'react';
import forecastGameService, {
  MAX_PINNED_BADGES,
} from '@/modules/calendar/services/forecast-game-service';
import {
  BADGE_FAMILIES,
  BADGES,
  BadgeDefinition,
  BadgeTier,
} from '@/modules/calendar/model/forecast-game/badges';
import BadgeMedal from '@/modules/calendar/components/forecast-game/badge-medal';
import { Loading } from '@/shared/components/export';
import styles from '@/modules/calendar/components/forecast-game/forecast-frenzy.module.scss';

const TIER_ORDER: BadgeTier[] = ['bronze', 'silver', 'gold'];

export default function ForecastFrenzyBadges() {
  const [earned, setEarned] = useState<string[]>();
  const [pinned, setPinned] = useState<string[]>([]);

  useEffect(() => {
    forecastGameService.getState().then(state => {
      setEarned(state.stats.badges);
      setPinned(state.pinnedBadges ?? []);
    });
  }, []);

  const togglePin = (id: string) => {
    forecastGameService.togglePinnedBadge(id).then(state => setPinned(state.pinnedBadges ?? []));
  };

  if (earned === undefined) {
    return <Loading />;
  }

  const uniqueBadges = BADGES.filter(badge => !badge.familyId);
  const atPinLimit = pinned.length >= MAX_PINNED_BADGES;

  return (
    <div>
      <div className={`${styles.mutedCaption} text-small mb-3`}>
        Pin up to {MAX_PINNED_BADGES} badges ({pinned.length}/{MAX_PINNED_BADGES}) to show off on
        Overview.
      </div>

      <div className={'mb-4'}>
        <div className={`fw-semibold mb-2 ${styles.recapTitle} text-content`}>Progression</div>
        {BADGE_FAMILIES.map(family => {
          const familyBadges = TIER_ORDER.map(tier =>
            BADGES.find(b => b.familyId === family.id && b.tier === tier),
          ).filter((b): b is BadgeDefinition => b !== undefined);

          return (
            <div key={family.id} className={styles.familyRow}>
              <div className={`${styles.familyLabel} text-small`}>{family.label}</div>
              <div className={styles.familyLadder}>
                {familyBadges.map((badge, i) => (
                  <Fragment key={badge.id}>
                    {i > 0 && <span className={styles.familyArrow}>→</span>}
                    <BadgeMedal
                      badge={badge}
                      unlocked={earned.includes(badge.id)}
                      pinned={pinned.includes(badge.id)}
                      pinDisabled={atPinLimit}
                      onTogglePin={() => togglePin(badge.id)}
                    />
                  </Fragment>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div>
        <div className={`fw-semibold mb-2 ${styles.recapTitle} text-content`}>
          Special Achievements
        </div>
        <div className={styles.badgeGrid}>
          {uniqueBadges.map(badge => (
            <BadgeMedal
              key={badge.id}
              badge={badge}
              unlocked={earned.includes(badge.id)}
              unique
              pinned={pinned.includes(badge.id)}
              pinDisabled={atPinLimit}
              onTogglePin={() => togglePin(badge.id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
